import { Badge } from "@/components/ui/badge";
import type {
	Timesheet,
	TimesheetBucketKind,
	TimesheetRow,
	TimesheetSettings,
} from "../../types";
import { formatCount, formatHoursMinutes, formatPct } from "../../utils/format";
import { BUCKET_KINDS, sumRows } from "../../utils/totals";

const BUCKET_LABELS: Record<TimesheetBucketKind, string> = {
	weekday: "Extras úteis",
	saturday: "Extras sáb.",
	sunday: "Extras dom.",
	holiday: "Extras feriado",
};

const SETTINGS_PCT: Record<TimesheetBucketKind, keyof TimesheetSettings> = {
	weekday: "weekdayPct",
	saturday: "saturdayPct",
	sunday: "sundayPct",
	holiday: "holidayPct",
};

function pendingLabel(count: number): string {
	return `${formatCount(count)} ${count === 1 ? "pendente" : "pendentes"}`;
}

function CountCell({ field, value }: { field: string; value: number }) {
	return (
		<td
			className="timesheet-table__count"
			data-field={field}
			data-empty={value === 0 ? "true" : undefined}
		>
			{formatCount(value)}
		</td>
	);
}

function MinutesCell({
	field,
	minutes,
	emphasis,
}: {
	field: string;
	minutes: number;
	emphasis?: boolean;
}) {
	return (
		<td
			className="timesheet-table__minutes"
			data-field={field}
			data-emphasis={emphasis ? "true" : undefined}
		>
			{formatHoursMinutes(minutes)}
		</td>
	);
}

function OvertimeCell({
	kind,
	minutes,
	pct,
}: {
	kind: TimesheetBucketKind;
	minutes: number;
	pct: number;
}) {
	return (
		<td
			className="timesheet-table__overtime"
			data-field={kind}
			data-empty={minutes === 0 ? "true" : undefined}
		>
			<span className="timesheet-table__overtime-minutes">
				{formatHoursMinutes(minutes)}
			</span>
			<span className="timesheet-table__overtime-pct">{formatPct(pct)}</span>
		</td>
	);
}

function WorkerRow({
	row,
	expanded,
	onSelect,
}: {
	row: TimesheetRow;
	expanded: boolean;
	onSelect: (row: TimesheetRow) => void;
}) {
	return (
		<tr
			data-worker-id={row.worker.id}
			data-pending={row.daysPending > 0 ? "true" : undefined}
		>
			<th scope="row" className="timesheet-table__worker">
				<button
					type="button"
					className="timesheet-table__worker-button"
					aria-expanded={expanded}
					aria-label={`Ver dias de ${row.worker.name}`}
					onClick={() => onSelect(row)}
				>
					<span className="timesheet-table__name">{row.worker.name}</span>
					{row.worker.jobTitle && (
						<span className="timesheet-table__job">{row.worker.jobTitle}</span>
					)}
				</button>
				{row.daysPending > 0 && (
					<Badge variant="warning" className="timesheet-table__pending">
						{pendingLabel(row.daysPending)}
					</Badge>
				)}
			</th>
			<CountCell field="present" value={row.daysPresent} />
			<CountCell field="absent" value={row.daysAbsent} />
			<CountCell field="medical_leave" value={row.daysMedicalLeave} />
			<CountCell field="day_off" value={row.daysDayOff} />
			<MinutesCell field="worked" minutes={row.workedMinutes} />
			<MinutesCell field="normal" minutes={row.normalMinutes} />
			<MinutesCell field="missing" minutes={row.missingMinutes} />
			{BUCKET_KINDS.map((kind) => (
				<OvertimeCell
					key={kind}
					kind={kind}
					minutes={row.overtime[kind].minutes}
					pct={row.overtime[kind].pct}
				/>
			))}
			<MinutesCell
				field="overtime_equivalent"
				minutes={row.overtimeEquivalentMinutes}
			/>
			<MinutesCell
				field="total_equivalent"
				minutes={row.totalEquivalentMinutes}
				emphasis
			/>
		</tr>
	);
}

export function TimesheetTable({
	sheet,
	selectedWorkerId,
	onSelectWorker,
}: {
	sheet: Timesheet;
	selectedWorkerId: string | undefined;
	onSelectWorker: (row: TimesheetRow) => void;
}) {
	const sums = sumRows(sheet.rows);
	return (
		<div className="timesheet-table__scroll">
			<table className="timesheet-table">
				<thead>
					<tr>
						<th className="timesheet-table__worker">Colaborador</th>
						<th>Presenças</th>
						<th>Faltas</th>
						<th>Atestados</th>
						<th>Folgas</th>
						<th>H. trabalhadas</th>
						<th>H. normais</th>
						<th>H. faltantes</th>
						{BUCKET_KINDS.map((kind) => (
							<th key={kind}>{BUCKET_LABELS[kind]}</th>
						))}
						<th>Extras equiv.</th>
						<th>Total equiv.</th>
					</tr>
				</thead>
				<tbody>
					{sheet.rows.map((row) => (
						<WorkerRow
							key={row.worker.id}
							row={row}
							expanded={row.worker.id === selectedWorkerId}
							onSelect={onSelectWorker}
						/>
					))}
				</tbody>
				<tfoot>
					<tr className="timesheet-table__totals">
						<th scope="row" className="timesheet-table__worker">
							Total
						</th>
						<CountCell field="present" value={sums.daysPresent} />
						<CountCell field="absent" value={sums.daysAbsent} />
						<CountCell field="medical_leave" value={sums.daysMedicalLeave} />
						<CountCell field="day_off" value={sums.daysDayOff} />
						<MinutesCell field="worked" minutes={sheet.totals.workedMinutes} />
						<MinutesCell field="normal" minutes={sums.normalMinutes} />
						<MinutesCell field="missing" minutes={sums.missingMinutes} />
						{BUCKET_KINDS.map((kind) => (
							<OvertimeCell
								key={kind}
								kind={kind}
								minutes={sums.overtimeByKind[kind]}
								pct={sheet.settings[SETTINGS_PCT[kind]]}
							/>
						))}
						<MinutesCell
							field="overtime_equivalent"
							minutes={sheet.totals.overtimeEquivalentMinutes}
						/>
						<MinutesCell
							field="total_equivalent"
							minutes={sums.totalEquivalentMinutes}
							emphasis
						/>
					</tr>
				</tfoot>
			</table>
		</div>
	);
}
